import React, { useState } from 'react';
import { Lock, Calendar, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';

/**
 * Modal shown when user tries to schedule into a full week
 * Provides options to select alternate week or override (PM/Ops only)
 */
export function CapacityFullModal({
  open,
  onClose,
  marketName,
  weekStartDate,
  nextAvailableWeeks = [],
  userCanOverride = false,
  onSelectWeek,
  onOverride,
  onPickAnother
}) {
  const [selectedWeek, setSelectedWeek] = useState(
    nextAvailableWeeks[0]?.week_start_date || ''
  );
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  
  // Format date for display
  const formatWeekDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  // Handle selecting an alternate week
  const handleUseSelected = () => {
    if (selectedWeek && onSelectWeek) {
      onSelectWeek(selectedWeek);
    }
  };
  
  // Handle override submission
  const handleOverride = () => {
    if (overrideReason.trim().length >= 10 && onOverride) {
      onOverride(overrideReason.trim());
    }
  };
  
  // Reset state when modal closes
  const handleClose = () => {
    setShowOverride(false);
    setOverrideReason('');
    setSelectedWeek(nextAvailableWeeks[0]?.week_start_date || '');
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md"
        data-testid="capacity-full-modal"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Lock className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <DialogTitle className="text-xl">That week is full</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-center text-muted-foreground">
            <span className="font-medium">{marketName || 'This territory'}</span> has no available 
            install slots for the week of{' '}
            <span className="font-medium">{formatWeekDate(weekStartDate)}</span>.
          </p>
          
          {/* Next available weeks */}
          {nextAvailableWeeks.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Next available weeks:
              </Label>
              
              <RadioGroup 
                value={selectedWeek} 
                onValueChange={setSelectedWeek}
                className="space-y-2"
              >
                {nextAvailableWeeks.map((week) => (
                  <label
                    key={week.week_start_date}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border cursor-pointer
                      transition-colors hover:bg-accent
                      ${selectedWeek === week.week_start_date ? 'border-primary bg-accent' : 'border-border'}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem 
                        value={week.week_start_date} 
                        id={week.week_start_date}
                      />
                      <span className="font-medium">
                        {formatWeekDate(week.week_start_date)}
                      </span>
                    </div>
                    <span className="text-sm text-emerald-600 dark:text-emerald-400">
                      {week.available_slots} slots
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          
          {/* Override section (PM/Ops only) */}
          {userCanOverride && (
            <div className="border-t pt-4 mt-4">
              <button
                onClick={() => setShowOverride(!showOverride)}
                className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 hover:underline"
                type="button"
              >
                <AlertTriangle className="w-4 h-4" />
                Override (PM/Ops only)
                {showOverride ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {showOverride && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-3">
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    You can schedule into this week anyway. An audit log entry will be created.
                  </p>
                  <div>
                    <Label htmlFor="override-reason" className="text-sm">
                      Override Reason <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="override-reason"
                      placeholder="Customer requested specific date due to..."
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      className="mt-1"
                      data-testid="override-reason-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum 10 characters required
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleOverride}
                    disabled={overrideReason.trim().length < 10}
                    className="w-full"
                    data-testid="override-submit-btn"
                  >
                    Override & Save
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onPickAnother || handleClose}
            data-testid="pick-another-btn"
          >
            Pick Another Date
          </Button>
          <Button
            onClick={handleUseSelected}
            disabled={!selectedWeek || nextAvailableWeeks.length === 0}
            data-testid="use-selected-btn"
          >
            Use Selected Week
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CapacityFullModal;
